import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  InputAdornment,
  alpha,
  LinearProgress
} from "@mui/material";
import CreditScoreIcon from "@mui/icons-material/CreditScore";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PrintIcon from "@mui/icons-material/Print";
import PrintReportHeader from "../../components/PrintReportHeader";
import LoanService from "../services/LoanService";
import { fNumber } from "../../utils/formatNumber";

export default function LoanOverview() {
  const { user: currentUser } = useSelector((state) => state.auth || {});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summaryData, setSummaryData] = useState(null);

  const getLast30DaysRange = () => {
    const now = new Date();
    const prior = new Date();
    prior.setDate(prior.getDate() - 30);
    const formatDate = (d) => d.toISOString().slice(0, 10);
    return {
      startDate: formatDate(prior),
      endDate: formatDate(now),
    };
  };

  // Calendar filter state (defaults to Last 30 Days)
  const [startDate, setStartDate] = useState(() => getLast30DaysRange().startDate);
  const [endDate, setEndDate] = useState(() => getLast30DaysRange().endDate);
  const [activePreset, setActivePreset] = useState("last30");

  // Tabs for detail views: 0 = Monthly Breakdown, 1 = Yearly Breakdown, 2 = Active Loan Debtors
  const [detailTab, setDetailTab] = useState(0);

  // Debtor search query
  const [debtorSearch, setDebtorSearch] = useState("");

  const fetchSummary = useCallback(async (start, end) => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (start) params.startDate = start;
      if (end) params.endDate = end;
      const res = await LoanService.getSummary(params);
      setSummaryData(res?.data || null);
    } catch (err) {
      console.error("fetchSummary error:", err);
      setError(err?.response?.data?.message || err?.message || "Failed to load loan summary");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary(startDate, endDate);
  }, [fetchSummary, startDate, endDate]);

  const setPresetRange = (preset) => {
    setActivePreset(preset);
    const now = new Date();
    if (preset === "all") {
      setStartDate("");
      setEndDate("");
    } else if (preset === "thisMonth") {
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
      setStartDate(`${year}-${month}-01`);
      setEndDate(`${year}-${month}-${String(lastDay).padStart(2, "0")}`);
    } else if (preset === "last30") {
      const prior = new Date();
      prior.setDate(prior.getDate() - 30);
      const formatDate = (d) => d.toISOString().slice(0, 10);
      setStartDate(formatDate(prior));
      setEndDate(formatDate(now));
    } else if (preset === "thisYear") {
      const year = now.getFullYear();
      setStartDate(`${year}-01-01`);
      setEndDate(now.toISOString().slice(0, 10));
    }
  };

  const handleCustomDateChange = (type, val) => {
    setActivePreset("custom");
    if (type === "start") setStartDate(val);
    if (type === "end") setEndDate(val);
  };

  // Filter debtors by search
  const filteredDebtors = useMemo(() => {
    const list = summaryData?.debtors || [];
    if (!debtorSearch.trim()) return list;
    const q = debtorSearch.toLowerCase().trim();
    return list.filter((u) => {
      const name = `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase();
      const uname = (u.username || "").toLowerCase();
      const phone = (u.phoneNumber || "").toLowerCase();
      return name.includes(q) || uname.includes(q) || phone.includes(q);
    });
  }, [summaryData, debtorSearch]);

  const recoveryRate = useMemo(() => {
    const disbursed = Number(summaryData?.totalDisbursed) || 0;
    const repaid = Number(summaryData?.totalRecovered) || 0;
    if (disbursed <= 0) return 0;
    return Math.min(100, Math.round((repaid / disbursed) * 100));
  }, [summaryData]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Official Print Header (Visible only during printing) */}
      <PrintReportHeader
        title="LOAN PORTFOLIO & CAPITAL STATEMENT"
        subtitle={
          detailTab === 0
            ? "Comprehensive monthly analysis of disbursed loans and repayment collections"
            : detailTab === 1
            ? "Comprehensive annual trend analysis of disbursed loans and repayment collections"
            : "Complete schedule of active member loan accounts and outstanding balances"
        }
        period={
          activePreset === "all"
            ? "All Time"
            : startDate && endDate
            ? `${startDate} to ${endDate}`
            : activePreset === "thisMonth"
            ? "This Month"
            : activePreset === "thisYear"
            ? "This Year"
            : activePreset === "last30"
            ? "Last 30 Days"
            : "Current Filter Period"
        }
        officer={
          currentUser
            ? `${currentUser.firstName || ''} ${currentUser.lastName || ''} (${currentUser.username || 'Officer'})`.trim()
            : "Loan Officer"
        }
        metrics={[
          { label: "Total Outstanding Loan", value: `₦${fNumber(summaryData?.totalOutstandingLoanBalance || 0)}`, color: "#DC2626" },
          { label: "Total Loans Disbursed", value: `₦${fNumber(summaryData?.totalDisbursed || 0)}`, color: "#0B3B60" },
          { label: "Total Recovered", value: `₦${fNumber(summaryData?.totalRecovered || 0)}`, color: "#059669" },
          { label: "Recovery Rate", value: `${recoveryRate}%`, color: recoveryRate >= 75 ? "#059669" : "#D97706" }
        ]}
      />

      {/* Header Banner */}
      <Box
        className="no-print"
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          background: "linear-gradient(135deg, #0B3B60 0%, #D97706 100%)",
          color: "#ffffff",
          boxShadow: "0 10px 25px -5px rgba(11, 59, 96, 0.25)",
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backdropFilter: "blur(4px)",
              }}
            >
              <CreditScoreIcon sx={{ color: "#ffffff", fontSize: 26 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
              Loan Portfolio &amp; Capital Overview
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.85)", ml: 6.5 }}>
            Real-time analytics on approved loans, repayments recovered, outstanding debt, and period trends.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{
              backgroundColor: "#ffffff",
              color: "#0B3B60",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "10px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
              "&:hover": { backgroundColor: "#F8FAFC" },
            }}
          >
            Print Statement
          </Button>
          <Button
            variant="contained"
            component={Link}
            to="/dashboard/financials"
            startIcon={<ArrowForwardIcon />}
            sx={{
              backgroundColor: "#ffffff",
              color: "#0B3B60",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "10px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
              "&:hover": { backgroundColor: "#F8FAFC" },
            }}
          >
            Manage Accounts
          </Button>
          <Button
            variant="outlined"
            onClick={() => fetchSummary(startDate, endDate)}
            sx={{
              color: "#ffffff",
              borderColor: "rgba(255,255,255,0.6)",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "10px",
              "&:hover": { borderColor: "#ffffff", backgroundColor: "rgba(255,255,255,0.1)" },
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Calendar Date Range & Presets Filter */}
      <Paper
        className="no-print"
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2.5,
          border: "1px solid #E2E8F0",
          backgroundColor: "#ffffff",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          {/* Custom Date Pickers */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <CalendarMonthIcon sx={{ color: "#D97706", fontSize: 22 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1E293B" }}>
                Filter Period:
              </Typography>
            </Box>

            <TextField
              type="date"
              size="small"
              label="Start Date"
              value={startDate}
              onChange={(e) => handleCustomDateChange("start", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160, backgroundColor: "#F8FAFC" }}
            />
            <Typography variant="body2" sx={{ color: "#64748B", fontWeight: 700 }}>
              to
            </Typography>
            <TextField
              type="date"
              size="small"
              label="End Date"
              value={endDate}
              onChange={(e) => handleCustomDateChange("end", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160, backgroundColor: "#F8FAFC" }}
            />

            {(startDate || endDate) && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setPresetRange("all")}
                sx={{
                  borderRadius: "8px",
                  borderColor: "#EF4444",
                  color: "#DC2626",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { borderColor: "#DC2626", backgroundColor: "rgba(239, 68, 68, 0.04)" },
                }}
              >
                Clear Dates
              </Button>
            )}
          </Box>

          {/* Quick Presets */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700 }}>
              Presets:
            </Typography>
            {[
              { key: "last30", label: "Last 30 Days" },
              { key: "thisMonth", label: "This Month" },
              { key: "thisYear", label: "This Year" },
              { key: "all", label: "All Time" },
            ].map(({ key, label }) => (
              <Button
                key={key}
                size="small"
                variant={activePreset === key ? "contained" : "outlined"}
                onClick={() => setPresetRange(key)}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.78rem",
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: activePreset === key ? "#D97706" : "#ffffff",
                  borderColor: activePreset === key ? "#D97706" : "#CBD5E1",
                  color: activePreset === key ? "#ffffff" : "#334155",
                  boxShadow: activePreset === key ? "0 2px 6px rgba(217, 119, 6, 0.25)" : "none",
                  "&:hover": {
                    backgroundColor: activePreset === key ? "#B45309" : "rgba(217, 119, 6, 0.06)",
                    borderColor: "#B45309",
                  },
                }}
              >
                {label}
              </Button>
            ))}
          </Box>
        </Box>
      </Paper>

      {/* Loading & Error States */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "#D97706" }} />
        </Box>
      ) : error ? (
        <Paper sx={{ p: 4, textAlign: "center", borderRadius: 3, border: "1px solid #FECACA", backgroundColor: "#FEF2F2" }}>
          <Typography color="error" sx={{ fontWeight: 600 }}>{error}</Typography>
        </Paper>
      ) : (
        <>
          {/* Top KPI Metric Cards */}
          <Grid container spacing={2.5} sx={{ mb: 3 }} className="no-print">
            {/* Card 1: Outstanding Loan Balance */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  border: "1px solid #E2E8F0",
                  backgroundColor: "#ffffff",
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "4px",
                    backgroundColor: "#DC2626",
                  },
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>
                    Total Outstanding Loan
                  </Typography>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "10px",
                      backgroundColor: "rgba(220, 38, 38, 0.08)",
                      color: "#DC2626",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AccountBalanceWalletIcon fontSize="small" />
                  </Box>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#DC2626", mb: 1 }}>
                  ₦{fNumber(summaryData?.totalOutstandingLoanBalance || 0)}
                </Typography>
                <Chip
                  label={`${summaryData?.activeDebtorsCount || 0} Members With Unpaid Loans`}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    backgroundColor: "rgba(220, 38, 38, 0.08)",
                    color: "#DC2626",
                    border: "1px solid rgba(220, 38, 38, 0.2)",
                  }}
                />
              </Paper>
            </Grid>

            {/* Card 2: Total Loan Capital Disbursed */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  border: "1px solid #E2E8F0",
                  backgroundColor: "#ffffff",
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "4px",
                    backgroundColor: "#0B3B60",
                  },
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>
                    Total Loan Capital Disbursed
                  </Typography>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "10px",
                      backgroundColor: alpha("#0B3B60", 0.08),
                      color: "#0B3B60",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <TrendingUpIcon fontSize="small" />
                  </Box>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0B3B60", mb: 1 }}>
                  ₦{fNumber(summaryData?.totalDisbursed || 0)}
                </Typography>
                <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600 }}>
                  Approved loans disbursed to members
                </Typography>
              </Paper>
            </Grid>

            {/* Card 3: Total Loan Capital Recovered */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  border: "1px solid #E2E8F0",
                  backgroundColor: "#ffffff",
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "4px",
                    backgroundColor: "#059669",
                  },
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>
                    Loan Capital Recovered (Repaid)
                  </Typography>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "10px",
                      backgroundColor: "rgba(5, 150, 105, 0.08)",
                      color: "#059669",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <TrendingDownIcon fontSize="small" />
                  </Box>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#059669", mb: 1 }}>
                  ₦{fNumber(summaryData?.totalRecovered || 0)}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={recoveryRate}
                    sx={{
                      flexGrow: 1,
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: "#E2E8F0",
                      "& .MuiLinearProgress-bar": { backgroundColor: "#059669" },
                    }}
                  />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#059669" }}>
                    {recoveryRate}%
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Card 4: Period Net & Transactions */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  border: "1px solid #E2E8F0",
                  backgroundColor: "#ffffff",
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "4px",
                    backgroundColor: "#D97706",
                  },
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>
                    Period Net Loan Capital
                  </Typography>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "10px",
                      backgroundColor: "rgba(217, 119, 6, 0.08)",
                      color: "#D97706",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CreditScoreIcon fontSize="small" />
                  </Box>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#D97706", mb: 1 }}>
                  ₦{fNumber(summaryData?.netOutstandingPeriod || 0)}
                </Typography>
                <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600 }}>
                  {summaryData?.transactionCount || 0} Total Loan Transactions
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Details Section with Tabs */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid #E2E8F0",
              backgroundColor: "#ffffff",
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
            }}
          >
            {/* Section Header for Printouts */}
            <Box className="print-only" sx={{ display: 'none', px: 2.5, py: 1.5, borderBottom: '1px solid #CBD5E1' }}>
              <Typography sx={{ fontSize: '11pt', fontWeight: 800, color: '#0B3B60', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                {detailTab === 0
                  ? "Monthly Loan Disbursements & Collections Breakdown"
                  : detailTab === 1
                  ? "Yearly Loan Disbursements & Collections Trend"
                  : "Active Member Loan Debtors Schedule"}
              </Typography>
            </Box>

            <Box
              className="no-print"
              sx={{
                px: 3,
                pt: 2,
                borderBottom: "1px solid #E2E8F0",
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                backgroundColor: "#F8FAFC",
              }}
            >
              <Tabs
                value={detailTab}
                onChange={(e, val) => setDetailTab(val)}
                textColor="primary"
                indicatorColor="primary"
                sx={{
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.9rem",
                    minHeight: 48,
                    color: "#64748B",
                    "&.Mui-selected": { color: "#0B3B60" },
                  },
                  "& .MuiTabs-indicator": { backgroundColor: "#0B3B60", height: 3 },
                }}
              >
                <Tab label={`Monthly Breakdown (${summaryData?.monthlyBreakdown?.length || 0})`} />
                <Tab label={`Yearly Breakdown (${summaryData?.yearlyBreakdown?.length || 0})`} />
                <Tab label={`Active Loan Debtors (${summaryData?.debtors?.length || 0})`} />
              </Tabs>

              {detailTab === 2 && (
                <TextField
                  size="small"
                  placeholder="Search debtor name/phone..."
                  value={debtorSearch}
                  onChange={(e) => setDebtorSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: "#94A3B8" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: 250,
                    mb: 1,
                    backgroundColor: "#ffffff",
                    "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                  }}
                />
              )}
            </Box>

            {/* TAB 0: Monthly Breakdown Table */}
            {detailTab === 0 && (
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: "#F1F5F9" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>Month</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: "#0B3B60", py: 1.5 }}>
                        Loans Disbursed
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: "#059669", py: 1.5 }}>
                        Repayments Recovered
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: "#D97706", py: 1.5 }}>
                        Net Difference
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>
                        Transactions
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>
                        Recovery Ratio
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(summaryData?.monthlyBreakdown || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4, color: "#64748B" }}>
                          No monthly loan transactions found for the selected period.
                        </TableCell>
                      </TableRow>
                    ) : (
                      summaryData.monthlyBreakdown.map((row) => {
                        const mRatio = row.credit > 0 ? Math.min(100, Math.round((row.debit / row.credit) * 100)) : 100;
                        return (
                          <TableRow key={row.month} hover sx={{ "&:hover": { backgroundColor: "#F8FAFC" } }}>
                            <TableCell sx={{ fontWeight: 700, color: "#0F172A", py: 1.5 }}>
                              {row.month}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: "#0B3B60" }}>
                              ₦{fNumber(row.credit)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: "#059669" }}>
                              ₦{fNumber(row.debit)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: row.net > 0 ? "#D97706" : "#059669" }}>
                              ₦{fNumber(row.net)}
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={row.count} size="small" sx={{ fontWeight: 600, fontSize: "0.75rem" }} />
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={mRatio}
                                  sx={{
                                    width: 80,
                                    height: 6,
                                    borderRadius: 3,
                                    backgroundColor: "#E2E8F0",
                                    "& .MuiLinearProgress-bar": { backgroundColor: mRatio >= 80 ? "#059669" : "#D97706" },
                                  }}
                                />
                                <Typography variant="caption" sx={{ fontWeight: 700, color: "#334155" }}>
                                  {mRatio}%
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* TAB 1: Yearly Breakdown Table */}
            {detailTab === 1 && (
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: "#F1F5F9" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>Year</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: "#0B3B60", py: 1.5 }}>
                        Total Loans Disbursed
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: "#059669", py: 1.5 }}>
                        Total Repayments Collected
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: "#D97706", py: 1.5 }}>
                        Net Outstanding for Year
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>
                        Transactions
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>
                        Annual Collection %
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(summaryData?.yearlyBreakdown || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4, color: "#64748B" }}>
                          No yearly loan transactions found for the selected period.
                        </TableCell>
                      </TableRow>
                    ) : (
                      summaryData.yearlyBreakdown.map((row) => {
                        const yRatio = row.credit > 0 ? Math.min(100, Math.round((row.debit / row.credit) * 100)) : 100;
                        return (
                          <TableRow key={row.year} hover sx={{ "&:hover": { backgroundColor: "#F8FAFC" } }}>
                            <TableCell sx={{ fontWeight: 800, color: "#0B3B60", py: 1.75, fontSize: "0.95rem" }}>
                              {row.year}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: "#0B3B60" }}>
                              ₦{fNumber(row.credit)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: "#059669" }}>
                              ₦{fNumber(row.debit)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: row.net > 0 ? "#DC2626" : "#059669" }}>
                              ₦{fNumber(row.net)}
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={row.count} size="small" sx={{ fontWeight: 600, fontSize: "0.75rem" }} />
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={yRatio}
                                  sx={{
                                    width: 100,
                                    height: 8,
                                    borderRadius: 4,
                                    backgroundColor: "#E2E8F0",
                                    "& .MuiLinearProgress-bar": { backgroundColor: yRatio >= 75 ? "#059669" : "#D97706" },
                                  }}
                                />
                                <Typography variant="caption" sx={{ fontWeight: 700, color: "#334155" }}>
                                  {yRatio}%
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* TAB 2: Active Loan Debtors Table */}
            {detailTab === 2 && (
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: "#F1F5F9" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>Member Name</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>Username</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>Phone Number</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: "#DC2626", py: 1.5 }}>
                        Outstanding Loan Debt
                      </TableCell>
                      <TableCell align="center" className="print-hide-action" sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredDebtors.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4, color: "#64748B" }}>
                          No members with active loan debt found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredDebtors.map((u) => {
                        const fullName = `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.username;
                        return (
                          <TableRow key={u._id} hover sx={{ "&:hover": { backgroundColor: "#F8FAFC" } }}>
                            <TableCell sx={{ fontWeight: 700, color: "#0F172A", py: 1.5 }}>
                              {fullName}
                            </TableCell>
                            <TableCell sx={{ color: "#64748B", fontSize: "0.85rem" }}>
                              @{u.username}
                            </TableCell>
                            <TableCell sx={{ color: "#334155", fontSize: "0.85rem" }}>
                              {u.phoneNumber || "—"}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800, color: "#DC2626", fontSize: "0.95rem" }}>
                              ₦{fNumber(u.loanBalance || 0)}
                            </TableCell>
                            <TableCell align="center" className="print-hide-action">
                              <Button
                                size="small"
                                variant="outlined"
                                component={Link}
                                to={`/dashboard/loan/${u._id}`}
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 600,
                                  fontSize: "0.75rem",
                                  borderRadius: "6px",
                                  borderColor: "#D97706",
                                  color: "#D97706",
                                  "&:hover": { backgroundColor: "rgba(217, 119, 6, 0.08)", borderColor: "#B45309" },
                                }}
                              >
                                View History
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}
    </Container>
  );
}
